/**
 * Distance in a job's score, by straight-line miles (founder decision, 2026-10-14, Part 6 of the Derek
 * build): living farther away lowers the score. Used only when BOTH the EMT's address and the job have
 * a map point; otherwise the state rule in calculateJobMatch applies, exactly as before.
 *
 *   within the EMT's own travel distance, or within 25 miles   100
 *   25 to 50 miles                                              85
 *   50 to 100 miles                                             70
 *   100 to 200 miles                                            45
 *   over 200 miles                                              20
 *   open to relocating and the job accepts relocation: never below 70.
 *
 * Shared verbatim with Cloud Functions (scripts/syncFunctionsGenerated.mjs), so the employer's number
 * is the EMT's number. Pure: no imports.
 */
export type MapPoint = { lat: number; lng: number };

export const DISTANCE_FREE_MILES = 25;
/** The bands after the free radius: up to `upTo` miles scores `score`. */
export const DISTANCE_BANDS: ReadonlyArray<{ upTo: number; score: number }> = [
  { upTo: 50, score: 85 },
  { upTo: 100, score: 70 },
  { upTo: 200, score: 45 },
];
export const DISTANCE_FAR_SCORE = 20;
export const RELOCATION_FLOOR = 70;
/** The travel distance field on job preferences accepts 0 to 500 miles. */
export const MAX_TRAVEL_RADIUS_MILES = 500;

function validPoint(lat: unknown, lng: unknown): boolean {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);
}

/** The EMT's saved map point: address.lat/lng (the account form), else the legacy geo field. */
export function emtMapPoint(profile: unknown): MapPoint | null {
  const p = (profile ?? {}) as { address?: { lat?: unknown; lng?: unknown }; geo?: { lat?: unknown; lng?: unknown } };
  if (validPoint(p.address?.lat, p.address?.lng)) return { lat: p.address!.lat as number, lng: p.address!.lng as number };
  if (validPoint(p.geo?.lat, p.geo?.lng)) return { lat: p.geo!.lat as number, lng: p.geo!.lng as number };
  return null;
}

/** The job's map point: jobLocation.lat/lng (the job form), else geo. */
export function jobMapPoint(job: unknown): MapPoint | null {
  const j = (job ?? {}) as { jobLocation?: { lat?: unknown; lng?: unknown } | null; geo?: { lat?: unknown; lng?: unknown } | null };
  if (validPoint(j.jobLocation?.lat, j.jobLocation?.lng)) return { lat: j.jobLocation!.lat as number, lng: j.jobLocation!.lng as number };
  if (validPoint(j.geo?.lat, j.geo?.lng)) return { lat: j.geo!.lat as number, lng: j.geo!.lng as number };
  return null;
}

export function straightLineMiles(a: MapPoint, b: MapPoint): number {
  const R = 3958.8;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** At most this many extra cities count (2026-10-15, B4). */
export const MAX_WORK_CITIES = 5;

/** A place distance can be measured from: home (label null) or one of the EMT's extra cities. */
export type EmtPlace = MapPoint & { label: string | null };

/** The EMT's extra cities with a map point (job preferences, picked from the city list), at most 5. */
export function emtWorkCityPoints(profile: unknown): EmtPlace[] {
  const list = (profile as { professional?: { jobPreferences?: { preferredLocationPoints?: unknown } } } | null)?.professional?.jobPreferences?.preferredLocationPoints;
  if (!Array.isArray(list)) return [];
  const out: EmtPlace[] = [];
  for (const raw of list) {
    const c = raw as { label?: unknown; city?: unknown; state?: unknown; lat?: unknown; lng?: unknown } | null;
    if (!c || !validPoint(c.lat, c.lng)) continue;
    const label = typeof c.label === 'string' && c.label.trim() ? c.label.trim() : [c.city, c.state].filter((x) => typeof x === 'string' && x).join(', ');
    out.push({ lat: c.lat as number, lng: c.lng as number, label: label || 'a city you added' });
    if (out.length >= MAX_WORK_CITIES) break;
  }
  return out;
}

/** Home first (when it has a map point), then the extra cities. */
export function emtPlaces(profile: unknown): EmtPlace[] {
  const home = emtMapPoint(profile);
  return [...(home ? [{ ...home, label: null }] : []), ...emtWorkCityPoints(profile)];
}

/** The nearest of home and the extra cities to a job, with the straight-line miles; null with none. */
export function nearestEmtPlace(profile: unknown, jobPoint: MapPoint): { place: EmtPlace; miles: number } | null {
  let best: { place: EmtPlace; miles: number } | null = null;
  for (const place of emtPlaces(profile)) {
    const miles = straightLineMiles(place, jobPoint);
    if (!best || miles < best.miles) best = { place, miles };
  }
  return best;
}

/** The EMT's own travel distance (job preferences), or null when not set. */
export function travelDistanceMiles(profile: unknown): number | null {
  const v = (profile as { professional?: { jobPreferences?: { maxTravelRadiusMiles?: unknown } } } | null)?.professional?.jobPreferences?.maxTravelRadiusMiles;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.min(v, MAX_TRAVEL_RADIUS_MILES) : null;
}

export type MilesScore = {
  score: number;
  miles: number;
  /** 'travel': inside the EMT's own travel distance; 'free': within 25 miles; else a band. */
  reason: 'travel' | 'free' | 'band' | 'far' | 'relocation';
  travelMiles: number | null;
};

export function distanceScoreForMiles(miles: number, travelMiles: number | null, relocationFloor: boolean): MilesScore {
  let score: number;
  let reason: MilesScore['reason'];
  if (travelMiles != null && miles <= travelMiles) {
    score = 100;
    reason = 'travel';
  } else if (miles <= DISTANCE_FREE_MILES) {
    score = 100;
    reason = 'free';
  } else {
    const band = DISTANCE_BANDS.find((b) => miles <= b.upTo);
    score = band ? band.score : DISTANCE_FAR_SCORE;
    reason = band ? 'band' : 'far';
  }
  if (relocationFloor && score < RELOCATION_FLOOR) {
    score = RELOCATION_FLOOR;
    reason = 'relocation';
  }
  return { score, miles, reason, travelMiles };
}

/**
 * The travel distance that would bring this job inside it (whole miles, rounded up to 5), or null when
 * none can (already inside, within 25 miles, or farther than the field allows).
 */
export function travelDistanceToReach(miles: number, travelMiles: number | null): number | null {
  if (miles <= DISTANCE_FREE_MILES || (travelMiles != null && miles <= travelMiles)) return null;
  const need = Math.ceil(miles / 5) * 5;
  return need <= MAX_TRAVEL_RADIUS_MILES ? need : null;
}
